import { useEffect, useState } from 'react'

import { t } from '../../../../../../../common/i18n.js'
import type { Language } from '../../../../../../../common/types/i18n.js'
import { Button } from '../../../../../../components/button/button.js'
import { Select } from '../../../../../../components/forms/select/select.js'
import { TextField } from '../../../../../../components/forms/text-field/text-field.js'
import { BaseModal } from '../../../../../../components/modal/base-modal/base-modal.js'
import { fetchEmployeeLeaveBundle, saveEmployeeLeaveBundle } from '../../../../utils/portal-api.js'

interface TimeAwayPolicyModalProps {
    isOpen: boolean
    onClose: () => void
    employeeId: number
    lang: Language
    /** Called after a successful save so the profile refetches its data. */
    onSaved: () => void
}

// TASK-348: change the employee's leave-policy bundle straight from the
// Eemalolek tab, with an optional effective date — the same assignment the
// Settings surface manages, gated by the same permission key server-side.
export const TimeAwayPolicyModal = ({
    isOpen,
    onClose,
    employeeId,
    lang,
    onSaved,
}: TimeAwayPolicyModalProps) => {
    const view = t.portal.timeOff.view

    const [bundles, setBundles] = useState<{ id: number; name: string }[]>([])
    const [bundleId, setBundleId] = useState<number | ''>('')
    const [effectiveFrom, setEffectiveFrom] = useState('')
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!isOpen) return
        let cancelled = false
        setLoading(true)
        setError(null)
        void (async () => {
            const result = await fetchEmployeeLeaveBundle(employeeId)
            if (cancelled) return
            if (result.ok) {
                setBundles(result.data.bundles)
                setBundleId(result.data.bundle_id ?? '')
                setEffectiveFrom(result.data.effective_from ?? '')
            } else {
                setError(result.error)
            }
            setLoading(false)
        })()
        return () => {
            cancelled = true
        }
    }, [isOpen, employeeId])

    const handleSave = async () => {
        setSaving(true)
        setError(null)
        try {
            const result = await saveEmployeeLeaveBundle(employeeId, {
                bundle_id: bundleId === '' ? null : bundleId,
                effective_from: effectiveFrom || null,
            })
            if (!result.ok) {
                setError(result.error)
                return
            }
            onClose()
            onSaved()
        } finally {
            setSaving(false)
        }
    }

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            disableBackdropClose={saving}
            className="portal-book-time-away-modal"
            showClose
            closeAriaLabel={t.portal.timeOff.close(lang)}
        >
            <div className="portal-book-time-away-modal__header">
                <h3 className="portal-book-time-away-modal__title">{view.policyEditTitle(lang)}</h3>
            </div>

            <div className="portal-book-time-away-modal__form-group">
                <label className="portal-book-time-away-modal__form-label">
                    {view.policyBundle(lang)}
                </label>
                <Select
                    className="portal-book-time-away-modal__form-select"
                    placeholder={view.policyNone(lang)}
                    value={bundleId === '' ? '' : String(bundleId)}
                    onChange={(value) => setBundleId(value ? Number(value) : '')}
                    options={bundles.map((b) => ({ value: String(b.id), label: b.name }))}
                    isDisabled={loading}
                />
            </div>

            <div className="portal-book-time-away-modal__form-group">
                <TextField
                    label={view.policyEffectiveFrom(lang)}
                    value={effectiveFrom}
                    onChange={setEffectiveFrom}
                    inputProps={{ type: 'date' }}
                    note={{ text: view.policyEffectiveFromHint(lang) }}
                    isDisabled={loading}
                />
            </div>

            {error && <div className="portal-book-time-away-modal__self-overlap">{error}</div>}

            <Button
                className="portal-book-time-away-modal__submit"
                size="medium"
                variant="primary"
                text={
                    saving
                        ? t.portal.settings.policies.saving(lang)
                        : t.portal.settings.policies.save(lang)
                }
                isDisabled={saving || loading}
                onClick={() => void handleSave()}
            />
        </BaseModal>
    )
}
